import { useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { useUserProfileState } from '#states/hooks.js';
import { Button } from '#ui/index.js';
import { IconSizes } from '#ui/MaterialIcon.js';
import { ShannonAvatar, ShannonIntroDialog } from '#views/trip/ShannonIntroDialog.js';

/**
 * "Chat with Shannon" support launcher (ELARA-750). Replaces the former Tidio
 * web widget with a WhatsApp deep link: human concierge support now lives in
 * WhatsApp on both web and mobile. Lives in the trip-specific navigation: the
 * desktop trip sidebar and the mobile trip tab bar. Pressing it shows the
 * {@link ShannonIntroDialog} — a short introduction to Shannon and what she can
 * help with — before handing off to WhatsApp.
 */
export function SupportButton({
  variant,
  size,
  className,
  labelClassName,
  labelInnerClassName,
  isMobile,
}: {
  variant: 'sidebar' | 'icon' | 'ghost';
  size: 'sidebar' | 'icon';
  className?: string;
  labelClassName?: string;
  labelInnerClassName?: string;
  isMobile?: boolean;
}) {
  const userProfileState = useUserProfileState();
  const [introOpen, setIntroOpen] = useState(false);

  if (!userProfileState.profile) return null;

  if (isMobile) {
    return (
      <>
        <button
          data-tour-highlight="nav-support"
          type="button"
          onClick={() => setIntroOpen(true)}
          className={cn(
            'relative cursor-pointer rounded-sm transition-text duration-200 font-medium px-3 pb-3 pt-0 flex-1',
            'flex items-center justify-center text-foreground',
          )}
        >
          <div className="flex items-center gap-1 flex-col">
            <ShannonAvatar size={IconSizes.md} />
            <span className="text-[10px] tracking-wider">Support</span>
          </div>
        </button>
        <ShannonIntroDialog open={introOpen} onOpenChange={setIntroOpen} />
      </>
    );
  }

  return (
    <>
      <Button
        data-tour-highlight="nav-support"
        variant={variant}
        size={size}
        className={className}
        onClick={() => setIntroOpen(true)}
        tooltip={variant === 'icon' ? 'Contact Shannon' : undefined}
        tooltipPosition="right"
      >
        <ShannonAvatar size={IconSizes.md} />
        {labelClassName && (
          <span className={labelClassName}>
            <span className={labelInnerClassName}>Contact Shannon</span>
          </span>
        )}
      </Button>
      <ShannonIntroDialog open={introOpen} onOpenChange={setIntroOpen} />
    </>
  );
}
